/** One invite's timeline (plan §11, D6): what the holder did on the signup page, what ops did with
 *  the invite, and — once it is used — what the tenant's users did in their dashboard until
 *  go-live. Oldest first, as `GET …/timeline/` returns it.
 *
 *  Signup rows have no actor (the holder has no account yet): they read "Invite holder", with the
 *  IP and place the row recorded. A marketplace app install's rows (it minted the invite, or connected
 *  the booking system again for it) read "Marketplace install". */
import { Alert, Box, Chip, Skeleton, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as invitesApi from '@/api/onboardingInvites';
import type { InviteTimelineItem } from '@/api/onboardingInvites';
import { TsValue } from '@/features/bgtasks/taskFormat';
import { restechWords } from '@/features/tour-operators/onboard/restechs/registry';
import { formatDate } from '@/utils/format';

export const INVITE_HOLDER = 'Invite holder';
export const MARKETPLACE_INSTALL = 'Marketplace install';

/** The words of the invite's own events (signup.* by the holder, onboarding_invite.* by ops).
 *  Anything else (the tenant's dashboard rows) shows its event name. */
export const TIMELINE_EVENT_LABELS: Record<string, string> = {
  'signup.opened': 'Opened the link',
  'signup.claimed': 'Started onboarding (claimed the link)',
  'signup.claim_refused': 'Refused: another browser holds the link',
  'signup.released': 'Released the link (continue on another device)',
  'signup.accounts_listed': 'Listed the booking-system accounts',
  'signup.account_viewed': 'Opened a booking-system account',
  'signup.duplicate_blocked': 'Refused: the company is already on TourOptima',
  'signup.email_code_sent': 'E-mail code sent',
  'signup.email_verified': 'E-mail verified',
  'signup.email_code_failed': 'Wrong or expired e-mail code',
  'signup.code_checked': 'Checked a tenant code',
  'signup.create_failed': 'Account creation failed',
  'signup.account_created': 'Created the tenant and the admin account',
  'signup.oauth_started': 'Started connecting the booking system',
  'signup.oauth_connected': 'Connected the booking system',
  'signup.oauth_failed': 'Connecting the booking system failed',
  'onboarding_invite.created': 'Invite created',
  'onboarding_invite.updated': 'Invite edited',
  'onboarding_invite.link_revealed': 'Link shown (copy / QR code)',
  'onboarding_invite.sent': 'Link e-mailed',
  'onboarding_invite.rotated': 'Code rotated',
  'onboarding_invite.revoked': 'Revoked',
  'onboarding_invite.reinstated': 'Reinstated',
  'onboarding_invite.released': 'Claim released by ops',
  'onboarding_invite.expiry_changed': 'Expiry changed',
};

/** A row a booking system's marketplace app install wrote: the invite it minted
 *  (`source: marketplace`) or a connection it held again for the invite (`via: marketplace`). */
function byMarketplace(item: InviteTimelineItem): boolean {
  const p = (item.payload ?? {}) as Record<string, unknown>;
  return !item.actor && (p.source === 'marketplace' || p.via === 'marketplace');
}

/** Who did it: the console / tenant user, a marketplace app install, or the holder of the link. */
export function timelineActor(item: InviteTimelineItem): string {
  if (item.actor) return item.actor.email || item.actor.name || `user #${item.actor.id}`;
  if (byMarketplace(item)) {
    const restech = ((item.payload ?? {}) as Record<string, unknown>).restech;
    return typeof restech === 'string' && restech
      ? `${MARKETPLACE_INSTALL} (${restechWords(restech).vendor})`
      : MARKETPLACE_INSTALL;
  }
  return item.source === 'invite' ? INVITE_HOLDER : 'System';
}

const str = (v: unknown): string => {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string') return v;
  return JSON.stringify(v);
};

/** The few payload facts worth a line, per event. Never more than the payload holds (it never
 *  carries a code, a link, a key or a password). */
export function timelineDetails(item: InviteTimelineItem): string | null {
  const p = (item.payload ?? {}) as Record<string, unknown>;
  switch (item.event) {
    case 'onboarding_invite.created':
      if (p.source === 'marketplace') return connection(p);
      return p.batch_id ? `batch ${str(p.batch_id)}` : null;
    case 'onboarding_invite.updated': {
      const fields = p.fields as Record<string, { before?: unknown; after?: unknown }> | undefined;
      if (!fields) return null;
      return Object.entries(fields)
        .map(([f, d]) => `${f}: ${str(d?.before)} → ${str(d?.after)}`)
        .join('; ');
    }
    case 'onboarding_invite.expiry_changed':
      return `${p.before ? formatDate(String(p.before)) : 'never'} → ${p.after ? formatDate(String(p.after)) : 'never'}`;
    case 'onboarding_invite.sent':
      return item.success ? (p.send_count ? `send #${str(p.send_count)}` : null) : `not sent (${str(p.outcome)})`;
    case 'onboarding_invite.rotated':
    case 'onboarding_invite.link_revealed':
      return p.code_version !== undefined ? `code version ${str(p.code_version)}` : null;
    case 'onboarding_invite.revoked':
      return [p.reason ? `“${str(p.reason)}”` : null, p.was ? `was ${str(p.was)}` : null].filter(Boolean).join(' · ') || null;
    case 'onboarding_invite.reinstated':
      return p.claim_released ? 'the interrupted claim was released' : null;
    case 'onboarding_invite.released':
      return p.claimed_ts ? `claimed since ${formatDate(String(p.claimed_ts))}${p.claim_ip ? ` from ${str(p.claim_ip)}` : ''}` : null;
    case 'signup.accounts_listed':
      return [p.restech, p.count !== undefined ? `${str(p.count)} account(s)` : null].filter(Boolean).map(str).join(' · ') || null;
    case 'signup.account_viewed':
      return [p.restech, p.account].filter(Boolean).map(str).join(' · ') || null;
    case 'signup.create_failed':
      return p.reason ? str(p.reason) : null;
    case 'signup.oauth_started':
      return [p.restech, p.label].filter(Boolean).map(str).join(' · ') || null;
    case 'signup.oauth_connected':
      return connection(p);
    case 'signup.oauth_failed':
      return [p.restech, p.stage, p.reason].filter(Boolean).map(str).join(' · ') || null;
    case 'signup.account_created':
      return p.tour_operator_id ? `tenant #${str(p.tour_operator_id)}` : null;
    default:
      return null;
  }
}

/** "bokun · fjord · account 3851": the booking-system account a connection names (never its
 *  credentials; the payload carries a fingerprint at most). */
function connection(p: Record<string, unknown>): string | null {
  const parts = [p.restech, p.label, p.account ? `account ${str(p.account)}` : null].filter(Boolean).map(str);
  return parts.join(' · ') || null;
}

function where(item: InviteTimelineItem): string | null {
  const place = [item.geo_city, item.geo_country].filter(Boolean).join(', ');
  if (!item.ip && !place) return null;
  return [item.ip, place].filter(Boolean).join(' · ');
}

function TimelineRow({ item }: { item: InviteTimelineItem }) {
  const details = timelineDetails(item);
  const place = item.actor ? null : where(item);
  return (
    <Box
      component="li"
      data-timeline-item={item.id}
      sx={{
        listStyle: 'none',
        borderLeft: 2,
        borderColor: item.success ? (item.source === 'tenant' ? 'success.light' : 'divider') : 'error.main',
        pl: 1.5,
        py: 0.75,
      }}
    >
      <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }} useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
          <TsValue value={item.created_ts} />
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {TIMELINE_EVENT_LABELS[item.event] ?? item.event}
        </Typography>
        {!item.success && <Chip size="small" color="error" variant="outlined" label="failed" sx={{ height: 18, fontSize: 11 }} />}
        {item.source === 'tenant' && (
          <Chip size="small" variant="outlined" color="success" label="tenant" sx={{ height: 18, fontSize: 11 }} />
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary" component="div">
        {timelineActor(item)}
        {place ? ` · ${place}` : ''}
      </Typography>
      {details && (
        <Typography variant="caption" component="div" sx={{ overflowWrap: 'anywhere' }}>
          {details}
        </Typography>
      )}
    </Box>
  );
}

export function InviteTimeline({ inviteId }: { inviteId: number }) {
  const query = useQuery({
    queryKey: invitesApi.invitesKeys.timeline(inviteId),
    queryFn: () => invitesApi.timeline(inviteId),
  });

  if (query.isLoading) return <Skeleton variant="rounded" height={96} />;
  if (query.isError || !query.data) return <Alert severity="error">Couldn’t load the timeline.</Alert>;

  const { items, sources, window: tenantWindow } = query.data;
  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        {sources.invite.count} invite event{sources.invite.count === 1 ? '' : 's'}
        {tenantWindow
          ? ` · ${sources.tenant.count} by the tenant's users from ${formatDate(tenantWindow.from)} to ${formatDate(tenantWindow.to)}`
          : ' · the tenant’s own activity appears here once the invite is used'}
      </Typography>
      {(sources.invite.truncated || sources.tenant.truncated) && (
        <Alert severity="info" sx={{ py: 0 }}>
          Only the newest 500 {sources.invite.truncated && sources.tenant.truncated ? 'rows of each source' : sources.invite.truncated ? 'invite rows' : 'tenant rows'} are listed.
        </Alert>
      )}
      {items.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Nothing has happened with this invite yet.
        </Typography>
      ) : (
        <Box component="ol" sx={{ m: 0, p: 0 }} aria-label="Invite timeline">
          {items.map((item) => (
            <TimelineRow key={`${item.source}-${item.id}`} item={item} />
          ))}
        </Box>
      )}
    </Stack>
  );
}
